import { IconButton } from '@assembly-js/design-system';
import { Typography } from '@mui/material';
import { FC, useState } from 'react';

import { HTMLInputEvent } from 'src/constants/dataTypes';
import { useAppDispatch } from 'src/hooks/useStore';
import { UploadButton } from 'src/legacy/components/Buttons/UploadButton';
import FileUtils from 'src/utils/FileUtils';
import S3Utils from 'src/utils/S3Utils';

interface FileUploaderProps {
  prePath: string;
  fileKey: string;
  onChangeFileKey: (key: string) => void;
  label?: string;
  startIcon?: React.ReactElement;
  useUploadPortalFile?: boolean;
  setS3FilePath?: (path: string) => void;
}

const FileUploader: FC<FileUploaderProps> = (props) => {
  const {
    prePath,
    fileKey,
    onChangeFileKey,
    label,
    startIcon,
    useUploadPortalFile = false,
    setS3FilePath,
  } = props;
  const dispatch = useAppDispatch();
  const [isUploading, setIsUploading] = useState(false);

  const onChange = async (e: HTMLInputEvent) => {
    const { files } = e.target;

    if (files && files.length) {
      const isoDate = new Date().toISOString();

      const path = `${prePath}/${isoDate}/${files.item(0)?.name}`;
      setIsUploading(true);
      const file = files.item(0);
      if (!file) {
        setIsUploading(false);
        return;
      }
      try {
        if (useUploadPortalFile) {
          const result = await S3Utils.uploadPortalFile(file, path);
          if (setS3FilePath) {
            setS3FilePath(result.Location);
          }
          onChangeFileKey(path);
        } else {
          const result = await S3Utils.uploadFile(
            path,
            file,
            { level: 'protected' },
            dispatch,
          );
          if (!result || !('key' in result)) {
            throw new Error('Failed to upload file to S3');
          }
          onChangeFileKey(result.key);
        }
      } catch {
        onChangeFileKey('');
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleRemoveFileKey = () => {
    onChangeFileKey('');
    if (useUploadPortalFile && setS3FilePath) {
      setS3FilePath('');
    }
  };

  if (!fileKey) {
    return (
      <UploadButton
        handleChange={onChange}
        isUploading={isUploading}
        label={label || 'Select'}
        htmlId="upload-btn"
        color="secondary"
        variant="contained"
        hasStartIcon={false}
        startIcon={startIcon}
      />
    );
  }

  return (
    <div className="flex items-center min-w-0">
      <Typography className="mr-2 max-w-[200px]" noWrap>
        {FileUtils.getFileNameFromPath(fileKey)}
      </Typography>

      <IconButton
        icon="Close"
        onClick={handleRemoveFileKey}
        size="sm"
        variant="minimal"
      />
    </div>
  );
};

export default FileUploader;
